/**
 * Subs' photo reports waiting on the PM, at the top of the Schedule tab (Duncan 2026-10-29, as his mockup: "If a project
 * manager only looks at the schedule and never goes to the task tab would they miss that fairly critical alert?"). E3
 * Subs makes a photo report for each finished line and files it in the sub's closeout Documents task; with none on the
 * job, they wait. The server counts them per sub (subProgress.js reportsWaiting).
 *
 * "Add the Documents task" opens Add Task already filled in -- type Documents, the sub as its Responsible Party, in the
 * Closeout phase -- for the PM to date and save. Once the sub has one, the row says the reports are on their way (E3 Subs
 * files them within a minute or two) until they're filed. "Not now" puts it off until more reports wait (a bell
 * dismissal of that count).
 */
import React, { useEffect, useRef, useState } from 'react';
import { Icon } from '../ui';
import { ErrorBanner, PrimaryButton, SecondaryButton } from './SubUi';
import { dismissNotification } from '../../api';

const smallBtn = { height: 30, fontSize: 13, padding: '0 12px' };
const s = (n) => (n === 1 ? '' : 's');

/** The task E3 Subs files a sub's photo reports in, as the server's isCloseoutDocs (E3 Subs' closeoutTaskOf): a
    Documents task in the Closeout phase or named for O&M manuals, as-builts or training -- never a submittals one. */
export const isCloseoutDocs = (t) => !!t && t.task_type === 'Documents' && !/submittal/i.test(t.task_name || '')
  && (/closeout/i.test(t.building_area_phase || '') || /o&m|as-?built|closeout|training/i.test(t.task_name || ''));

/** What Add Task opens with for a sub's closeout papers: named for its one trade (as "Lighting O&M Manuals, As-Builts &
    Training"), else for the sub. */
export const closeoutTaskFor = (r) => {
  const trades = Array.isArray(r.trades) ? r.trades.filter(Boolean) : [];
  return {
    task_name: `${trades.length === 1 ? trades[0] : r.sub_name} O&M Manuals, As-Builts & Training`,
    task_type: 'Documents',
    building_area_phase: 'Closeout',
    responsible_party: r.sub_name,
    subcontractor_id: r.sub_id,
  };
};

function ReportRow({ report, filingIn, code, focused, busy, onAdd, onNotNow }) {
  const ref = useRef(null);
  useEffect(() => {
    if (focused && ref.current) ref.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [focused]);
  const n = report.waiting;
  return (
    <div ref={ref} style={{
      display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) auto', gap: 10, alignItems: 'center', padding: '10px 14px',
      borderTop: '1px solid var(--border-subtle)', background: focused ? 'var(--warning-soft)' : undefined,
    }}>
      <div style={{ minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-1)' }}>{report.sub_name}</span>
          <span style={{ fontSize: 12, fontWeight: 600, padding: '2px 9px', borderRadius: 999, background: 'var(--warning-soft)', color: 'var(--warning)' }}>
            {n} photo report{s(n)}
          </span>
        </div>
        <div style={{ fontSize: 13, color: 'var(--text-2)', marginTop: 4, lineHeight: 1.5 }}>
          {filingIn ? (
            <>
              <b style={{ fontWeight: 600, color: 'var(--text-1)' }}>On their way. </b>
              E3 Subs files {n === 1 ? 'it' : 'them'} in {code ? `${code} ` : ''}{filingIn.task_name} within a minute or two.
            </>
          ) : (
            <>
              E3 Subs makes a photo report for each finished line and files it in the sub’s closeout Documents task. This job
              has no Documents task for {report.sub_name}, so the {n === 1 ? 'report is' : `${n} reports are`} waiting.
            </>
          )}
        </div>
      </div>
      {!filingIn && (
        <span style={{ display: 'inline-flex', gap: 6, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
          <PrimaryButton onClick={onAdd} style={smallBtn}>Add the Documents task</PrimaryButton>
          <SecondaryButton disabled={busy} onClick={onNotNow} style={smallBtn}>Not now</SecondaryButton>
        </span>
      )}
    </div>
  );
}

/**
 * The banner: each sub on this job with photo reports waiting. Nothing when there are none. `tasks`: the schedule's own,
 * so a Documents task just added shows at once (before E3 Subs files). `codeOf(taskId)`: a task's code as the list shows
 * it. `focused`: a sub's id or 'all' (the bell). `onAddTask(prefill)` opens Add Task; `onChanged` after "Not now".
 */
export default function SubReportsBanner({ reports, tasks, codeOf, focused, onAddTask, onChanged }) {
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState(null);
  if (!reports || !reports.length) return null;
  const filingOf = (r) => r.closeout_task
    || (tasks || []).filter((t) => Number(t.subcontractor_id) === Number(r.sub_id)).find(isCloseoutDocs) || null;

  const notNow = async (r) => {
    setBusy(r.sub_id); setError(null);
    try {
      await dismissNotification({ alert_key: r.key, action: 'read' });
      onChanged();
    } catch (e) {
      setError(e.message || 'Couldn’t put it off.');
    } finally {
      setBusy(null);
    }
  };

  return (
    <div id="sub-reports" style={{
      marginBottom: 14, borderRadius: 12, overflow: 'hidden', background: 'var(--bg-surface)',
      border: '1px solid color-mix(in oklch, var(--warning) 35%, transparent)',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 14px', background: 'var(--warning-soft)' }}>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7, fontSize: 14.5, fontWeight: 600, color: 'var(--warning)' }}>
          <Icon name="camera" size={15} />
          Photo reports waiting on you · {reports.length} sub{s(reports.length)}
        </span>
      </div>
      {reports.map((r) => (
        <ReportRow key={r.key} report={r} filingIn={filingOf(r)} busy={busy === r.sub_id}
          code={filingOf(r) ? (codeOf && codeOf(filingOf(r).id)) || filingOf(r).task_code : null}
          focused={focused != null && focused !== 'all' && Number(focused) === Number(r.sub_id)}
          onAdd={() => onAddTask(closeoutTaskFor(r))} onNotNow={() => notNow(r)} />
      ))}
      {error && <div style={{ padding: '0 14px 10px' }}><ErrorBanner>{error}</ErrorBanner></div>}
      <div style={{ padding: '8px 14px', borderTop: '1px solid var(--border-subtle)', fontSize: 12.5, color: 'var(--text-3)' }}>
        Add the Documents task opens a new task already filled in: type Documents, the sub as its Responsible Party, in the
        Closeout phase. Set its dates and save; E3 Subs files the reports a minute or two later. The bell shows these on the
        job too.
      </div>
    </div>
  );
}
